<template>
  <modal
    :show.sync="showModal"
    :disable-background-close="true"
    @close="handleClose"
  >
    <div v-if="person">
      <div class="modal-header">
        <h5 class="header-name" @click="goToPerson(person.id)">
          {{ person.name }}
        </h5>
        <div class="header-details">
          <badge :class="getBadgeClass(person.involvement)">
            {{ getAbbreviatedInvolvement(person.involvement) }}
          </badge>
          <span><strong>{{ person.gender }}</strong></span>
          <span><strong>{{ person.school_year }}</strong></span>
          <span><strong>{{ getColumnNameFromId(person.recruitment_depth_chart_column_id) }}</strong></span>
          <span v-if="person.is_greek"><strong>Greek</strong></span>
          <span v-if="person.is_varsity"><strong>Varsity</strong></span>
          <span>Last updated: {{ formattedLastUpdatedAt }}</span>
        </div>
      </div>
      <div
        class="modal-body"
        v-if="loading"
        v-loading="loading"
      >
      </div>
      <div v-else class="modal-body">
        <el-form>
          <el-form-item>
            Notes
            <el-input
              type="textarea"
              v-model="notes"
              :autosize="{ minRows: 3, maxRows: 5 }"
              placeholder="Add notes here..."
            ></el-input>
          </el-form-item>
          <el-form-item>
            <div class="open-to-call-row">
              <!-- Asks the team director for their own call, so they do not go and consult the student -->
              <span class="open-to-call-label">Are you open to a recruitment manager calling this student?</span>
              <div class="open-to-call-controls">
                <el-radio-group v-model="openToRecruitmentManagerCall">
                  <el-radio :label="true">
                    Yes
                  </el-radio>
                  <el-radio :label="false">
                    No
                  </el-radio>
                </el-radio-group>
                <el-button
                  v-if="openToRecruitmentManagerCall !== null"
                  type="text"
                  icon="el-icon-close"
                  class="clear-open-to-call"
                  @click="openToRecruitmentManagerCall = null"
                >
                  Clear
                </el-button>
              </div>
              <div v-if="openToRecruitmentManagerCall === true" class="open-to-call-followup">
                <div :class="['phone-check', savedPhone ? 'phone-check-found' : 'phone-check-missing']">
                  <i :class="savedPhone ? 'el-icon-phone-outline' : 'el-icon-warning-outline'"></i>
                  <div class="phone-check-body">
                    <strong v-if="savedPhone">Please verify this number is current.</strong>
                    <strong v-else>No phone number on file &mdash; add one so a recruitment manager can call.</strong>
                    <div v-if="canEditPhone" class="phone-edit">
                      <el-input
                        v-model="phoneDraft"
                        size="small"
                        class="phone-input"
                        placeholder="e.g. 303-555-0147"
                        @keyup.enter.native="savePhone()"
                      ></el-input>
                      <!-- Space for these is reserved, so editing the number never resizes the input -->
                      <div class="phone-edit-actions">
                        <template v-if="phoneHasChanged">
                          <el-button
                            type="primary"
                            size="mini"
                            icon="el-icon-check"
                            circle
                            :disabled="wouldClearExistingPhone"
                            :title="wouldClearExistingPhone
                              ? 'Enter a number, or discard to restore the current one'
                              : 'Save number'"
                            :loading="savingPhone"
                            @click="savePhone()"
                          ></el-button>
                          <el-button
                            size="mini"
                            icon="el-icon-close"
                            circle
                            title="Discard change"
                            @click="phoneDraft = savedPhone"
                          ></el-button>
                        </template>
                      </div>
                    </div>
                    <span v-else-if="savedPhone" class="phone-readonly">
                      {{ formattedPersonPhone }}
                    </span>
                    <span v-else class="phone-readonly">
                      Add one on {{ person.name }}'s profile.
                    </span>
                  </div>
                </div>
                <div class="call-priority">
                  <span class="call-priority-label">
                    How much recruitment manager involvement would you request?
                  </span>
                  <!-- Options stack vertically so each one keeps its description on a single line -->
                  <div class="call-priority-controls">
                    <!-- The label prop is the stored value; only the text beside it is descriptive -->
                    <el-radio-group
                      v-model="recruitmentManagerCallPriority"
                      class="call-priority-options"
                    >
                      <el-radio label="High">
                        High <span class="call-priority-hint">&mdash; first in line for calls</span>
                      </el-radio>
                      <el-radio label="Medium">
                        Medium <span class="call-priority-hint">&mdash; would like some communication</span>
                      </el-radio>
                      <el-radio label="Low">
                        Low <span class="call-priority-hint">&mdash; take care of the others first</span>
                      </el-radio>
                    </el-radio-group>
                  </div>
                  <!-- Below the options, not beside them: the descriptions leave no room on the row -->
                  <div class="call-priority-clear">
                    <el-button
                      v-if="recruitmentManagerCallPriority !== null"
                      type="text"
                      icon="el-icon-close"
                      class="clear-open-to-call"
                      @click="recruitmentManagerCallPriority = null"
                    >
                      Clear
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </el-form-item>
          <el-form-item>
            If declined invitation to apply for FOCUS staff, please select the reason(s) below:
            <el-select v-model="selectedReasons" multiple placeholder="Select reason(s)">
              <el-option
                v-for="reason in reasons"
                :key="reason.value"
                :label="reason.label"
                :value="reason.value"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="selectedReasons && selectedReasons.includes(reasons.find(reason => reason.label === 'Other').value)"
          >
            If other, please provide more information:
            <el-input
              type="text"
              v-model="otherReasons"
              placeholder="Specify other reasons..."
            ></el-input>
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          type="submit"
          class="btn btn-info"
          @click="saveNotes"
          v-loading="saving"
        >Save</el-button>
        <el-button class="btn" @click.native="handleClose">Close</el-button>
      </span>
    </div>
  </modal>
</template>

<script>
import { Badge } from 'src/components/UIComponents';
import { mapState, mapGetters } from 'vuex';
import Modal from '../UIComponents/Modal';
import { RepositoryFactory } from '../../repositories/repositoryFactory';

const RecruitmentDepthChartRepository = RepositoryFactory.get('recruitmentDepthCharts');

export default {
    name: 'RecruitmentNotesModal',
    components: {
        Badge,
        Modal
    },
    data() {
        return {
            showModal: false,
            notes: '', // Initial notes are empty
            selectedReasons: [], // An array to hold multiple selections
            otherReasons: '', // Holds the custom reason if "other" is selected
            openToRecruitmentManagerCall: null, // null = no action taken, true/false = explicit answer
            recruitmentManagerCallPriority: null, // 'High' | 'Medium' | 'Low', only applies when open to a call
            personPhone: null, // cell number currently on file, shown so staff can verify it before an RM calls
            phoneDraft: '', // what is in the editable phone field, saved separately from the notes
            canEditPhone: false, // whether this user is allowed to change the person's contact details
            savingPhone: false,
            reasons: [ // Possible reasons for declined invitation
                { label: 'MPD', value: 78 },
                { label: 'Dating Fast', value: 79 },
                { label: 'Relocation', value: 80 },
                { label: 'Other Job Opportunity', value: 81 },
                { label: 'Fear of Career Impact', value: 82 },
                { label: 'Continuing Education', value: 83 },
                { label: 'Does Not Feel Called', value: 84 },
                { label: 'Pursuing Religious Vocation', value: 85 },
                { label: 'Other', value: 86 }
            ],
            lastUpdatedAt: '',
            saving: false,
            loading: false,
        };
    },
    computed: {
        ...mapState({
            locations: (state) => state.userLocations,
        }),
        ...mapGetters({
            locationFromStore: 'lastLocation'
        }),
        locationId: {
            get() {
                return this.locationFromStore;
            },
            set(newLocation) {
                this.$store.commit('setLastLocation', newLocation);
            }
        },
        formattedLastUpdatedAt() {
            if (!this.lastUpdatedAt) return '';
            let date;
            if (this.lastUpdatedAt.includes('T')) {
                // ISO format
                date = new Date(this.lastUpdatedAt);
            } else {
                // Legacy format: 'YYYY-MM-DD HH:mm:ss'
                const isoUtc = `${this.lastUpdatedAt.replace(' ', 'T')}Z`;
                date = new Date(isoUtc);
            }
            // Format without seconds
            const options = {
                year: 'numeric', month: 'short', day: 'numeric',
            };
            return date.toLocaleString(undefined, options);
        },
        // The number on file, normalised: compare and display against this rather than the raw value, so
        // a padded or non-string number from the API cannot make an untouched field look edited or throw
        savedPhone() {
            return this.personPhone === null || this.personPhone === undefined
                ? ''
                : String(this.personPhone).trim();
        },
        phoneHasChanged() {
            return this.phoneDraft.trim() !== this.savedPhone;
        },
        // Emptying the field would remove the number from the person's profile, which this form does not do
        wouldClearExistingPhone() {
            return this.phoneDraft.trim() === '' && this.savedPhone !== '';
        },
        formattedPersonPhone() {
            if (!this.savedPhone) return '';
            const digits = this.savedPhone.replace(/\D/g, '');
            // Format US numbers for readability, otherwise show whatever is on file as-is
            if (digits.length === 10) {
                return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
            }
            if (digits.length === 11 && digits.startsWith('1')) {
                return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
            }
            return this.savedPhone;
        },
    },
    watch: {
        // React to changes in the prop from the parent
        show(val) {
            this.showModal = val;
            if (this.showModal) {
                this.getRecruitmentNotes();
                this.getLastUpdatedAt();
            }
        },
        // A priority only makes sense alongside a 'yes', so drop it if that answer changes
        openToRecruitmentManagerCall(val) {
            if (val !== true) {
                this.recruitmentManagerCallPriority = null;
            }
        }
    },
    props: {
        show: Boolean,
        person: {
            type: Object,
            // eslint-disable-next-line
            default: {}
        },
        getBadgeClass: {
            type: Function,
            required: true
        },
        getAbbreviatedInvolvement: {
            type: Function,
            required: true
        },
    },
    methods: {
        handleClose() {
            this.$emit('close');
            this.notes = '';
            this.selectedReasons = [];
            this.otherReasons = '';
            this.openToRecruitmentManagerCall = null;
            this.recruitmentManagerCallPriority = null;
            this.personPhone = null;
            this.phoneDraft = '';
            this.canEditPhone = false;
            this.savingPhone = false;
        },
        savePhone(announce = true) {
            if (!this.phoneHasChanged || this.savingPhone) return Promise.resolve();
            // This form corrects a number, it does not remove one. Emptying the field would delete it
            // from the person's profile, so restore it and send them to the profile to do that properly.
            if (this.wouldClearExistingPhone) {
                this.phoneDraft = this.savedPhone;
                // eslint-disable-next-line
                this.$message.info(`To remove a phone number, edit it on ${this.person.name}'s profile.`);
                return Promise.resolve();
            }
            this.savingPhone = true;
            return RecruitmentDepthChartRepository.updatePersonPhone(this.person.id, this.phoneDraft.trim())
                .then((response) => {
                    this.savingPhone = false;
                    this.personPhone = response.data.phone || null;
                    this.phoneDraft = this.savedPhone;
                    if (announce) {
                        this.$message(`Successfully updated phone number for ${this.person.name}`);
                    }
                })
                .catch((error) => {
                    this.savingPhone = false;
                    const message = error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : `There was an error updating the phone number for ${this.person.name}`;
                    this.$message.error(message);
                    throw error;
                });
        },
        saveNotes() {
            // Check if the column_id is 'declined-invitation' and selectedReasons is empty
            const needToSpecifyDeclinedInvitationReason = this.doTheyNeedToSpecifyAReasonForDeclinedInvitation();
            if (needToSpecifyDeclinedInvitationReason) {
                // eslint-disable-next-line
                this.$message.error('Please select at least one reason they may have indicated for declining an invitation to apply for FOCUS staff.');
                return; // Prevent the method from proceeding
            }
            this.saving = true;
            // Persist an edited phone number alongside the notes so a typed change is never silently lost.
            // The phone save reports its own errors, so bail out here rather than closing the modal.
            this.savePhone(false).then(() => (
                RecruitmentDepthChartRepository.storeRecruitmentNotes({
                    personId: this.person.id,
                    notes: this.notes,
                    declinedInvitationReasonIds: this.selectedReasons,
                    otherReasons: this.otherReasons,
                    openToRecruitmentManagerCall: this.openToRecruitmentManagerCall,
                    recruitmentManagerCallPriority: this.recruitmentManagerCallPriority,
                    locationId: this.locationId
                }).then((response) => {
                    this.saving = false;
                    this.handleClose();
                    this.$message(`Successfully saved notes for ${this.person.name}`);
                    // Emit event to parent to update last updated time
                    this.$emit('note-saved');
                }).catch((error) => {
                    this.saving = false;
                    this.handleClose();
                    this.$message.error(error.response.data.message);
                })
            )).catch(() => {
                this.saving = false;
            });
        },
        getColumnNameFromId(id) {
            // Map the recruitment depth chart column id to the column name
            // can be either id string or number string based on user interaction
            switch (id) {
            case 72:
                return 'Potential Candidate';
            case 73:
                return 'Top Candidate';
            case 74:
                return 'Invited to Apply';
            case 75:
                return 'Applied';
            case 76:
                return 'Declined Invitation';
            case 77:
                return 'Not Invited to Apply';
            case 95:
                return 'Hired';
            case 96:
                return 'Not Hired';
            case 'potential-candidates':
                return 'Potential Candidate';
            case 'top-candidates':
                return 'Top Candidate';
            case 'invited-to-apply':
                return 'Invited to Apply';
            case 'applied':
                return 'Applied';
            case 'declined-invitation':
                return 'Declined Invitation';
            case 'not-invited-to-apply':
                return 'Not Invited to Apply';
            case 'hired':
                return 'Hired';
            case 'not-hired':
                return 'Not Hired';
            default:
                return id;
            }
        },
        getRecruitmentNotes() {
            this.loading = true;
            RecruitmentDepthChartRepository.getPersonsRecruitmentNotes(this.person.id)
                .then((response) => {
                    this.notes = response.data.notes;
                    this.selectedReasons = response.data.declined_invitation_reasons_ids || [];
                    this.otherReasons = response.data.other_reasons;
                    const openToCall = response.data.open_to_recruitment_manager_call;
                    this.openToRecruitmentManagerCall = typeof openToCall === 'boolean' ? openToCall : null;
                    this.recruitmentManagerCallPriority = response.data.recruitment_manager_call_priority || null;
                    this.personPhone = response.data.phone || null;
                    this.phoneDraft = this.savedPhone;
                    this.canEditPhone = response.data.can_edit_phone === true;
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.handleClose();
                    this.$message.error(`There was an error fetching notes for ${this.person.name}`);
                });
        },
        getLastUpdatedAt() {
            if (this.person) {
                RecruitmentDepthChartRepository.getMemberLastUpdatedAt(this.person.id)
                    .then((response) => {
                        this.lastUpdatedAt = response.data.last_updated_at;
                    })
                    .catch((error) => {
                        console.error(`Error fetching last updated at for ${this.person.name}:`, error);
                    });
            }
        },
        goToPerson(id) {
            this.$router.push(`/people/${id}`);
        },
        doTheyNeedToSpecifyAReasonForDeclinedInvitation() {
            // Check if the column_id is 'declined-invitation' and selectedReasons is empty
            // eslint-disable-next-line
            if (this.getColumnNameFromId(this.person.recruitment_depth_chart_column_id) === 'Declined Invitation' && this.selectedReasons.length === 0) {
                return true;
            }
            return false;
        }
    },
};
</script>

  <style scoped>
  .badge-na {
    background-color: #4d4f54; /* Starting color */
    color: white;
}

.badge-bspx1 {
  background-color: #6bb0e1; /* BSPx1 - Light blue */
  color: white;
}

.badge-bspx4 {
  background-color: #5aa4d9; /* Intermediate color */
  color: white;
}

.badge-growing-disciple {
  background-color: #4b97d1; /* Intermediate color */
  color: white;
}

.badge-commissioned-disciple {
  background-color: #3d89c9; /* Intermediate color */
  color: white;
}

.badge-slbs {
  background-color: #307bc1; /* Intermediate color */
  color: white;
}

.badge-growing-disciple-maker {
  background-color: #236eb9; /* Intermediate color */
  color: white;
}

.badge-disciple-maker {
  background-color: #0c3c61; /* Dark blue */
  color: white;
}
.modal-header {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10px;
}

.header-details {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap; /* Allow wrapping as needed */
}
.header-details > span {
  white-space: nowrap; /* Prevents the gender from wrapping */
}
.modal-body {
  display: flex;
}

/* Fill the modal rather than sizing to the widest child, which would widen the modal itself */
.modal-body .el-form {
  flex: 1 1 auto;
  min-width: 0;
}
.dialog-footer {
  text-align: right;
  display: flex;
  justify-content: flex-end; /* Aligns the content to the end of the flex container */
}

.header-name:hover {
  cursor: pointer;
  color: #0c3c61;
}

.open-to-call-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.open-to-call-label {
  line-height: 1.4;
}

.open-to-call-controls {
  display: flex;
  align-items: center;
  /* Yes/No plus the clear button is narrow enough to always sit on one line */
  flex-wrap: nowrap;
  gap: 10px;
  line-height: 1.4;
  min-height: 28px; /* fixed height so the row doesn't grow with the clear button */
}

.open-to-call-controls .el-radio-group {
  flex: 0 0 auto; /* the clear button never squeezes the radios onto a second line */
}

.open-to-call-followup {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-top: 4px;
}

.phone-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  border-left: 3px solid;
  line-height: 1.4;
  font-size: 13px;
}

.phone-check i {
  flex: 0 0 auto;
  font-size: 16px;
  line-height: 1.4;
}

.phone-check-found {
  background-color: #eaf2f9;
  border-left-color: #0c3c61;
  color: #0c3c61;
}

.phone-check-missing {
  background-color: #fdf6ec;
  border-left-color: #e6a23c;
  color: #a76b12;
}

.phone-check-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* Fills the space left by the icon; a width of 100% would push its contents past the box */
  flex: 1 1 auto;
  min-width: 0;
}

.phone-edit {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px; /* height stays put as the confirm/discard buttons appear */
}

.phone-input {
  /* Takes the space the button slot leaves, so its width doesn't change when they appear */
  flex: 1 1 auto;
  min-width: 0;
  max-width: 190px;
}

.phone-edit-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 64px; /* room for both mini circle buttons, reserved whether or not they show */
}

/* Element's default sibling margin would push the pair past the reserved width */
.phone-edit-actions .el-button + .el-button {
  margin-left: 0;
}

.phone-readonly {
  font-weight: 600;
}

.call-priority {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.call-priority-label {
  line-height: 1.4;
}

.call-priority-controls {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.call-priority-options {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  flex: 0 0 auto; /* the clear button never squeezes an option onto a second line */
}

/* Stacked, so Element's inline gap between radios only steals width from the description */
.call-priority-options .el-radio {
  margin-right: 0;
}

/* Secondary text explaining an option, kept smaller so the row still fits the modal */
.call-priority-hint {
  color: #909399;
  font-size: 12px;
}

/* Height is reserved, so selecting an option does not shift the rows below it */
.call-priority-clear {
  min-height: 20px;
}

.clear-open-to-call {
  padding: 0;
  font-size: 12px;
  color: #909399;
}

.clear-open-to-call:hover,
.clear-open-to-call:focus {
  color: #0c3c61;
}

</style>
